import { useMemo } from 'react';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { generatePath } from '@/services/router/generate-path';
import { useLocation } from '@/services/router/compat';
import type { KnownRoute } from '@/services/router';
import type { LocalCommand } from './local-commands';
import type { PermissionGuard } from './use-navigation-search';
import { matchesLocalCommand } from './utils/search-commands';
import {
  addRoutingCommand,
  getNumericRouteParamFromPathname,
  matchesCurrentWorkspaceRoute,
  type Navigate,
} from './routing-command-utils';

type BuildSegmentRoutingCommandsArgs = {
  query: string;
  workspaceId: number;
  pathname: string;
  guard: PermissionGuard | null;
  navigate: Navigate;
  peoplePlural: string;
  peopleRelativePronoun: string;
};

type UseSegmentRoutingCommandsDeps = {
  workspaceId: number;
  guard: PermissionGuard | null;
  navigate: Navigate;
};

// The journeys URL covers every tab, the embedded app's included. The canonical URL has no bare
// segment route, so it is matched by the tabs this app serves there.
const SEGMENT_DETAIL_ROUTES = [
  '/workspaces/:workspaceId/journeys/segments/:segmentId',
  '/workspaces/:workspaceId/segments/:segmentId/overview',
  '/workspaces/:workspaceId/segments/:segmentId/people',
  '/workspaces/:workspaceId/segments/:segmentId/usage',
  '/workspaces/:workspaceId/segments/:segmentId/audiences',
] as const satisfies readonly KnownRoute[];
// Both URL shapes of the list; the journeys one forwards to the other with the flag on.
const SEGMENT_LIST_ROUTES = [
  '/workspaces/:workspaceId/segments',
  '/workspaces/:workspaceId/journeys/segments',
] as const satisfies readonly KnownRoute[];
const SEGMENT_STATIC_ROUTES = [
  ...SEGMENT_LIST_ROUTES,
  '/workspaces/:workspaceId/journeys/segments/new',
  '/workspaces/:workspaceId/journeys/segments/explore',
  '/workspaces/:workspaceId/journeys/segments/explore/people',
] as const satisfies readonly KnownRoute[];
const MAX_TYPED_SEGMENT_ROUTING_COMMANDS = 4;

export function getSegmentIdFromPathname(pathname: string, workspaceId: number): string | null {
  for (const route of SEGMENT_DETAIL_ROUTES) {
    const segmentId = getNumericRouteParamFromPathname({ pathname, workspaceId, route, paramName: 'segmentId' });
    if (segmentId != null) return segmentId;
  }
  return null;
}

export function isSegmentPathname(pathname: string, workspaceId: number): boolean {
  if (workspaceId === 0) return false;
  if (getSegmentIdFromPathname(pathname, workspaceId) != null) return true;
  return SEGMENT_STATIC_ROUTES.some((route) => matchesCurrentWorkspaceRoute(pathname, route, workspaceId));
}

export function buildSegmentRoutingCommands({
  query,
  workspaceId,
  pathname,
  guard,
  navigate,
  peoplePlural,
  peopleRelativePronoun,
}: BuildSegmentRoutingCommandsArgs): LocalCommand[] {
  if (workspaceId === 0) return [];
  if (!isSegmentPathname(pathname, workspaceId)) return [];

  const trimmedQuery = query.trim();
  const isEmptyQuery = trimmedQuery.length === 0;

  const segmentId = getSegmentIdFromPathname(pathname, workspaceId);
  const canViewSegments = guard?.hasPermission('journeys/segments/view') === true;
  const canCreateSegments = guard?.hasPermission('journeys/segments/create') === true;
  const canEditSegments = guard?.hasPermission('journeys/segments/edit') === true;
  const isBuilder = isBuilderPlan(guard?.account);
  const commands: LocalCommand[] = [];

  if (segmentId != null && canViewSegments) {
    const segmentRouteParams = { workspaceId, segmentId };

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/overview', segmentRouteParams),
      {
        id: `segment-routing:${segmentId}:overview`,
        label: 'Open segment overview',
        description: 'Open the segment overview',
        icon: 'segment',
        keywords: ['segment', 'overview', 'details'],
      },
    );

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/people', segmentRouteParams),
      {
        id: `segment-routing:${segmentId}:people`,
        label: `View ${peoplePlural} in segment`,
        description: `View ${peoplePlural} ${peopleRelativePronoun} match this segment`,
        icon: 'segment',
        keywords: ['segment', 'people', 'members', 'audience'],
      },
    );

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/usage', segmentRouteParams),
      {
        id: `segment-routing:${segmentId}:usage`,
        label: 'View segment usage',
        description: 'View where this segment is used',
        icon: 'segment',
        keywords: ['segment', 'usage', 'used by', 'dependencies'],
      },
    );

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/audiences', segmentRouteParams),
      {
        id: `segment-routing:${segmentId}:audiences`,
        label: 'View ad audiences',
        description: 'View ad audiences connected to this segment',
        icon: 'segment',
        keywords: ['segment', 'audiences', 'ads', 'ad audiences'],
      },
    );
  }

  if (segmentId != null && canEditSegments) {
    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/overview/edit', {
        workspaceId,
        segmentId,
      }),
      {
        id: `segment-routing:${segmentId}:edit`,
        label: 'Edit segment',
        description: "Change this segment's conditions",
        icon: 'edit',
        keywords: ['segment', 'edit', 'conditions'],
      },
    );
  }

  if (canCreateSegments) {
    if (!isBuilder) {
      addRoutingCommand(
        commands,
        pathname,
        navigate,
        generatePath(
          '/workspaces/:workspaceId/journeys/segments/explore',
          { workspaceId },
          { type: 'data-driven', source: 'segments' },
        ),
        {
          id: 'segment-routing:create-data-driven',
          label: 'Create data-driven segment',
          description: `Automatically add or remove ${peoplePlural} based on conditions`,
          icon: 'segment-data-driven',
          keywords: ['segment', 'data driven', 'dynamic', 'conditions', 'explorer'],
        },
      );
    }

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/new', { workspaceId }, { type: 'manual' }),
      {
        id: 'segment-routing:create-manual',
        label: 'Create manual segment',
        description: `Add ${peoplePlural} via list uploads, workflows, or the API`,
        icon: 'segment-manual',
        keywords: ['segment', 'manual', 'static', 'upload', 'api'],
      },
    );
  }

  const isOnList = SEGMENT_LIST_ROUTES.some((route) => matchesCurrentWorkspaceRoute(pathname, route, workspaceId));
  if (canViewSegments && !isOnList) {
    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments', { workspaceId }),
      {
        id: 'segment-routing:index',
        label: 'Go to segments',
        description: 'Browse all segments',
        icon: 'segment',
        keywords: ['segment', 'segments', 'list'],
      },
    );
  }

  const matchingCommands = commands.filter(matchesLocalCommand(query));

  if (isEmptyQuery) return matchingCommands;

  return matchingCommands.slice(0, MAX_TYPED_SEGMENT_ROUTING_COMMANDS);
}

export function useSegmentRoutingCommands(query: string, deps: UseSegmentRoutingCommandsDeps): LocalCommand[] {
  const { pathname } = useLocation();
  const { workspaceId, guard, navigate } = deps;

  return useMemo(
    () =>
      buildSegmentRoutingCommands({
        query,
        workspaceId,
        pathname,
        guard,
        navigate,
        peoplePlural: 'people',
        peopleRelativePronoun: 'who',
      }),
    [query, workspaceId, pathname, guard, navigate],
  );
}
